/**
 * Login Page
 *
 * Simple login page with "Sign in with Google" button.
 * No username/password form - we use Google OAuth exclusively.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { Card, Button, ThemeToggle, Modal } from '../components';
import { authApi } from '../services/api';

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, setUser, setToken, loadUserFromStorage } = useAuthStore();

  // Modal state for error messages
  const [modalState, setModalState] = useState({
    isOpen: false,
    title: '',
    message: '',
    variant: 'info' as 'info' | 'warning' | 'danger',
  });

  // Check if user is already authenticated
  useEffect(() => {
    loadUserFromStorage();
  }, [loadUserFromStorage]);

  // If already authenticated, redirect to appropriate page
  useEffect(() => {
    if (isAuthenticated) {
      const redirect = searchParams.get('redirect') || searchParams.get('redirect_to');

      if (redirect) {
        // Check if it's an external URL
        if (redirect.startsWith('http://') || redirect.startsWith('https://')) {
          // Cross-service services (e.g. monitor.hitsearch.biz) authenticate
          // via the .hitsearch.biz cookie, NOT localStorage and NOT URL tokens.
          // If localStorage says we're authenticated but the cookie is missing
          // (expired, cleared, blocked), redirecting will just bounce us right
          // back here — that is the infinite-loop bug. Clear the stale state
          // and let the user click "Sign in with Google" to re-establish the
          // cookie via the backend.
          const hasCookie = document.cookie
            .split('; ')
            .some(row => row.startsWith('auth_token='));

          if (!hasCookie) {
            console.warn('[LoginPage] localStorage has token but .hitsearch.biz cookie is missing — clearing stale state.');
            setToken(null);
            setUser(null);
            return;
          }

          // Strip any stale `token` param that may have leaked into the URL
          // from a previous redirect cycle, so we don't produce ?token=X&token=Y.
          let cleanRedirect = redirect;
          try {
            const url = new URL(redirect);
            url.searchParams.delete('token');
            cleanRedirect = url.toString();
          } catch {
            // Not a parseable URL — keep original.
          }

          const token = localStorage.getItem('auth_token');
          window.location.href = `${cleanRedirect}${cleanRedirect.includes('?') ? '&' : '?'}token=${token}`;
          return;
        } else {
          // Internal redirect - use navigate
          navigate(redirect);
          return;
        }
      }

      // Default: go to services
      navigate('/services');
    }
  }, [isAuthenticated, navigate, searchParams, setToken, setUser]);

  // Handle OAuth callback (when user returns from Google)
  useEffect(() => {
    const token = searchParams.get('token');
    const error = searchParams.get('error');
    const email = searchParams.get('email');

    if (error) {
      let title = 'Login Failed';
      let message = 'Login failed. Please try again.';
      let variant: 'info' | 'warning' | 'danger' = 'danger';

      if (error === 'account_disabled') {
        title = 'Account Disabled';
        message = `Your account (${email}) has been disabled by an administrator.\n\nPlease contact support if you believe this is an error.`;
        variant = 'danger';
      } else if (error === 'pending_approval') {
        title = 'Pending Approval';
        message = `Your account (${email}) is pending administrator approval.\n\nYou will receive an email once your account is activated.`;
        variant = 'warning';
      } else if (error === 'access_denied') {
        title = 'Authentication Cancelled';
        message = 'Google authentication was cancelled or denied.';
        variant = 'info';
      } else if (error === 'invalid_callback') {
        title = 'Invalid Callback';
        message = 'Invalid authentication callback. Please try again.';
        variant = 'danger';
      } else {
        message = `Login failed: ${error}`;
      }

      setModalState({ isOpen: true, title, message, variant });

      // Clear error from URL
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('error');
      newParams.delete('email');
      window.history.replaceState({}, '', `${window.location.pathname}?${newParams}`);
      return;
    }

    if (token) {
      // OAuth successful - store token and fetch user profile
      setToken(token);
      authApi.getProfile()
        .then((user) => {
          setUser(user);
          const redirect = searchParams.get('redirect') || searchParams.get('redirect_to');
          navigate(redirect || '/services');
        })
        .catch((err) => {
          console.error('Failed to fetch user profile:', err);
          setModalState({
            isOpen: true,
            title: 'Login Failed',
            message: 'Failed to fetch user profile. Please try again.',
            variant: 'danger',
          });
        });
    }
  }, [searchParams, setUser, setToken, navigate]);

  const handleGoogleLogin = async () => {
    const redirectTo = searchParams.get('redirect') || searchParams.get('redirect_to');
    const authUrl = await authApi.getGoogleAuthUrl(redirectTo || undefined);
    window.location.href = authUrl;
  };

  const pageStyles: React.CSSProperties = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'var(--bg-primary)',
    padding: '20px',
    position: 'relative',
  };

  const themeToggleContainerStyles: React.CSSProperties = {
    position: 'absolute',
    top: '20px',
    right: '20px',
  };

  const containerStyles: React.CSSProperties = {
    maxWidth: '400px',
    width: '100%',
  };

  const titleStyles: React.CSSProperties = {
    fontSize: '24px',
    fontWeight: 600,
    textAlign: 'center',
    marginBottom: '8px',
    color: 'var(--text-primary)',
  };

  const subtitleStyles: React.CSSProperties = {
    fontSize: '14px',
    textAlign: 'center',
    marginBottom: '32px',
    color: 'var(--text-secondary)',
  };

  const googleButtonStyles: React.CSSProperties = {
    backgroundColor: 'var(--bg-secondary)',
    border: '1px solid var(--border-color)',
    color: 'var(--text-primary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: 500,
    width: '100%',
  };

  return (
    <div style={pageStyles}>
      <div style={themeToggleContainerStyles}>
        <ThemeToggle />
      </div>

      <div style={containerStyles}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <img src="/logo-280x45.png"   alt="HitSearch" className="logo-light" style={{ height: '45px', width: 'auto' }} />
          <img src="/credits-logo2.png" alt="HitSearch" className="logo-dark"  style={{ height: '45px', width: 'auto' }} />
        </div>

        <Card>
          <h1 style={titleStyles}>Welcome to HitSearch</h1>
          <p style={subtitleStyles}>
            Sign in to access all HitSearch services
          </p>

          <Button
            onClick={handleGoogleLogin}
            fullWidth
            style={googleButtonStyles}
          >
            <svg width="18" height="18" viewBox="0 0 18 18">
              <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
              <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/>
              <path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707 0-.593.102-1.167.282-1.707V4.961H.957C.347 6.175 0 7.548 0 9s.348 2.825.957 4.039l3.007-2.332z"/>
              <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
            </svg>
            Sign in with Google
          </Button>

          <div style={{ marginTop: '24px', textAlign: 'center', fontSize: '12px', color: 'var(--text-secondary)' }}>
            By signing in, you agree to our Terms of Service and Privacy Policy
          </div>
        </Card>
      </div>

      <Modal
        isOpen={modalState.isOpen}
        onClose={() => setModalState({ ...modalState, isOpen: false })}
        title={modalState.title}
        message={modalState.message}
        variant={modalState.variant}
        confirmText="OK"
      />
    </div>
  );
};
